/**
 * Sync orchestration: pull, merge, push.
 *
 * The layout is two documents, per plan.md §5.2 — `config.json` (people, cards,
 * categories, rules, budgets, settings: small and written often) and
 * `data.json` (transactions and statements: large, append-mostly). Splitting
 * them means renaming a category does not rewrite the whole spend history.
 *
 * Every write is conditional on the eTag we last read. A 412 means the other
 * device wrote first: we re-read, merge again, and retry once. If it still
 * conflicts, we stop and tell the user rather than looping or overwriting.
 */

import {
  FutureSchemaError,
  SCHEMA_VERSION,
  parseConfigDocument,
  parseDataDocument,
  type ConfigDocument,
  type DataDocument,
} from '../../domain/schema.ts';
import {
  applyTombstones,
  hasRemoteChanged,
  mergeById,
  mergeTombstones,
  mergeTransactions,
  pruneTombstones,
  mergeStamped,
  newerRecord,
  canonicalJson,
} from '../../domain/merge.ts';
import * as repo from '../repo.ts';
import { ConflictError, readDocument, writeDocument } from './graph.ts';
import { AuthExpiredError, getAccount, InteractionStartedError } from './msal.ts';
import type { SyncMeta } from '../db.ts';
import type { Tombstone, TombstoneCollection } from '../../domain/types.ts';
import { db } from '../db.ts';

export const CONFIG_DOCUMENT = 'config.json';
export const DATA_DOCUMENT = 'data.json';

export type SyncStatus =
  | 'ok'
  | 'not-configured'
  | 'not-signed-in'
  /** The cached session is spent; only signing in again fixes it. */
  | 'auth-expired'
  | 'conflict'
  | 'future-schema'
  | 'offline'
  | 'error';

export interface SyncOutcome {
  status: SyncStatus;
  /** Transactions that arrived from OneDrive. */
  pulled: number;
  /** Transactions this device contributed. */
  pushed: number;
  /** Rows where the remote copy was newer and replaced the local one. */
  updated: number;
  /** Config changed on both sides; merged additively, worth mentioning. */
  configBothChanged: boolean;
  detail: string | null;
}

function outcome(status: SyncStatus, detail: string | null = null): SyncOutcome {
  return { status, pulled: 0, pushed: 0, updated: 0, configBothChanged: false, detail };
}

async function readLocal() {
  const [transactions, statements, people, cards, categories, rules, budgets, settings] =
    await Promise.all([
      repo.listTransactions(),
      repo.listStatements(),
      repo.listPeople(),
      repo.listCards(),
      repo.listCategories(),
      repo.listRules(),
      repo.listBudgets(),
      repo.getSettings(),
    ]);
  const tombstones = await repo.listTombstones();
  return { transactions, statements, people, cards, categories, rules, budgets, settings, tombstones };
}

type LocalState = Awaited<ReturnType<typeof readLocal>>;

/**
 * Each document carries only the tombstones for the collections it owns, so a
 * deleted rule cannot arrive without the rules it applies to.
 */
const CONFIG_COLLECTIONS = ['people', 'cards', 'categories', 'rules'] as const;
const DATA_COLLECTIONS = ['transactions', 'statements'] as const;

function tombstonesFor(
  tombstones: readonly Tombstone[],
  collections: readonly TombstoneCollection[],
): Tombstone[] {
  return tombstones.filter((t) => collections.includes(t.collection));
}

function buildConfigDocument(local: LocalState): ConfigDocument {
  return parseConfigDocument({
    schemaVersion: SCHEMA_VERSION,
    updatedAt: new Date().toISOString(),
    people: local.people,
    cards: local.cards,
    categories: local.categories,
    rules: local.rules,
    budgets: local.budgets,
    settings: local.settings,
    tombstones: tombstonesFor(local.tombstones, CONFIG_COLLECTIONS),
  });
}

function buildDataDocument(
  local: Pick<LocalState, 'transactions' | 'statements' | 'tombstones'>,
): DataDocument {
  return {
    schemaVersion: SCHEMA_VERSION,
    updatedAt: new Date().toISOString(),
    transactions: local.transactions,
    statements: local.statements,
    tombstones: tombstonesFor(local.tombstones, DATA_COLLECTIONS),
  };
}

/**
 * One full sync cycle. Safe to call repeatedly; a no-op sync costs two GETs.
 */
export async function syncNow(): Promise<SyncOutcome> {
  const account = await getAccount().catch(() => null);
  if (account === null) return outcome('not-signed-in');

  if (!navigator.onLine) return outcome('offline');

  let meta = await repo.getSyncMeta();

  // A different account means the stored eTags describe someone else's files.
  if (meta.accountId !== null && meta.accountId !== account.homeAccountId) {
    meta = { ...meta, configETag: null, dataETag: null, lastSyncedAt: null };
  }

  try {
    return await runSync(meta, account.homeAccountId, true);
  } catch (error) {
    if (error instanceof InteractionStartedError) {
      // Consent lapsed and MSAL is navigating to the sign-in page. The page is
      // going away; there is nothing to report.
      return outcome('not-signed-in');
    }
    if (error instanceof AuthExpiredError) {
      // Reported as its own status so the badge can offer the one action that
      // works, rather than a failure the user has to diagnose.
      return outcome('auth-expired', error.message);
    }
    if (error instanceof FutureSchemaError) {
      return outcome('future-schema', error.message);
    }
    if (error instanceof ConflictError) {
      return outcome('conflict', error.documentName);
    }
    return outcome('error', error instanceof Error ? error.message : String(error));
  }
}

async function runSync(
  meta: SyncMeta,
  accountId: string,
  mayRetry: boolean,
): Promise<SyncOutcome> {
  const [remoteConfigDoc, remoteDataDoc] = await Promise.all([
    readDocument(CONFIG_DOCUMENT),
    readDocument(DATA_DOCUMENT),
  ]);

  // Parsing runs migrations forward and refuses anything written by a newer
  // build, rather than silently dropping fields it does not understand.
  const remoteConfig =
    remoteConfigDoc === null ? null : parseConfigDocument(remoteConfigDoc.content);
  const remoteData = remoteDataDoc === null ? null : parseDataDocument(remoteDataDoc.content);

  const snapshot = await db.transaction('rw', db.tables, async () => {
    meta = await repo.getSyncMeta();
    if (meta.accountId !== null && meta.accountId !== accountId) {
      meta = { ...meta, configETag: null, dataETag: null, lastSyncedAt: null };
    }
    const local = await readLocal();

    // Check whether remote files changed since our last successful sync. A device
    // that has never synced holds no eTag, so every existing remote document
    // counts as changed — otherwise the authoritative-local branches below would
    // throw away a document this device has never read.
    const remoteConfigChanged = hasRemoteChanged(remoteConfigDoc?.eTag ?? null, meta.configETag);
    const remoteDataChanged = hasRemoteChanged(remoteDataDoc?.eTag ?? null, meta.dataETag);

    // A first sync is not a divergence: local is dirty only because the defaults
    // were just seeded. Warning there would fire on every new browser.
    const configBothChanged = remoteConfigChanged && meta.dirty && meta.configETag !== null;

    // When remote data has not changed since our last sync and local is dirty, local transactions & statements
    // reflect the user's latest local choices — including any local transaction deletions.
    // In this case, local data is authoritative so deleted transactions are not resurrected from OneDrive.
    const transactions =
      !remoteDataChanged && meta.dirty
        ? { merged: local.transactions, addedFromRemote: 0, addedFromLocal: 0, updatedFromRemote: 0 }
        : mergeTransactions(local.transactions, remoteData?.transactions ?? []);
    const statements =
      !remoteDataChanged && meta.dirty
        ? { merged: local.statements, addedFromRemote: 0, addedFromLocal: 0, updatedFromRemote: 0 }
        : mergeById(local.statements, remoteData?.statements ?? []);

    // When remote config has not changed since our last sync and local is dirty, local config
    // (people, cards, categories, rules) reflects the user's latest local choices —
    // including any local deletions. In this case, local config is authoritative so
    // deleted items are not resurrected from OneDrive.
    const people =
      !remoteConfigChanged && meta.dirty
        ? { merged: local.people, addedFromRemote: 0, addedFromLocal: 0, updatedFromRemote: 0 }
        : mergeStamped(local.people, remoteConfig?.people ?? []);
    const cards =
      !remoteConfigChanged && meta.dirty
        ? { merged: local.cards, addedFromRemote: 0, addedFromLocal: 0, updatedFromRemote: 0 }
        : mergeStamped(local.cards, remoteConfig?.cards ?? []);
    const categories =
      !remoteConfigChanged && meta.dirty
        ? { merged: local.categories, addedFromRemote: 0, addedFromLocal: 0, updatedFromRemote: 0 }
        : mergeStamped(local.categories, remoteConfig?.categories ?? []);
    const rules =
      !remoteConfigChanged && meta.dirty
        ? { merged: local.rules, addedFromRemote: 0, addedFromLocal: 0, updatedFromRemote: 0 }
        : mergeStamped(local.rules, remoteConfig?.rules ?? []);

    /*
     * Tombstones are unioned from both sides and then applied *after* every
     * collection merge — the union is precisely what puts deleted rows back, so
     * applying them earlier would accomplish nothing.
     *
     * Pruned here rather than on write, so an old tombstone stops being honoured
     * and stops being republished in the same pass.
     */
    const tombstones = pruneTombstones(
      mergeTombstones(local.tombstones, [
        ...(remoteConfig?.tombstones ?? []),
        ...(remoteData?.tombstones ?? []),
      ]),
      new Date(),
    );

    /*
     * Written before `applyMerged`, not after: that call runs the orphan cleanup,
     * which deletes rows and records tombstones of its own. Replacing the set
     * afterwards would erase them, and those rows would come back next sync.
     */
    await repo.replaceTombstones(tombstones);

    await repo.applyMerged({
      /*
       * Both of these pass a write stamp: their ids are content-derived, so a
       * re-import re-creates the very id an earlier deletion tombstoned, and a
       * row written after that deletion must not be deleted again.
       */
      transactions: applyTombstones(
        transactions.merged,
        tombstones,
        'transactions',
        (row) => row.updatedAt,
      ),
      statements: applyTombstones(
        statements.merged,
        tombstones,
        'statements',
        (row) => row.importedAt,
      ),
      people: applyTombstones(people.merged, tombstones, 'people'),
      cards: applyTombstones(cards.merged, tombstones, 'cards'),
      categories: applyTombstones(categories.merged, tombstones, 'categories'),
      rules: applyTombstones(rules.merged, tombstones, 'rules'),
      budgets: mergeStamped(local.budgets, remoteConfig?.budgets ?? []).merged,
      settings: remoteConfig ? newerRecord(local.settings, remoteConfig.settings) : local.settings,
    });

    const mergedLocal = await readLocal();
    return { mergedLocal, transactions, configBothChanged, revision: meta.revision };
  });
  const { mergedLocal, transactions, configBothChanged, revision } = snapshot;

  // Only write what actually changed remotely, so an idle sync is two GETs.
  const contentEqual = (left: unknown[], right: unknown[]) =>
    canonicalJson([...left].sort((a, b) => (a as { id: string }).id.localeCompare((b as { id: string }).id))) ===
    canonicalJson([...right].sort((a, b) => (a as { id: string }).id.localeCompare((b as { id: string }).id)));
  const configNeedsPush = remoteConfig === null ||
    !contentEqual(mergedLocal.people, remoteConfig.people) ||
    !contentEqual(mergedLocal.cards, remoteConfig.cards) ||
    !contentEqual(mergedLocal.categories, remoteConfig.categories) ||
    !contentEqual(mergedLocal.rules, remoteConfig.rules) ||
    !contentEqual(mergedLocal.budgets, remoteConfig.budgets) ||
    canonicalJson(mergedLocal.settings) !== canonicalJson(remoteConfig.settings) ||
    !contentEqual(tombstonesFor(mergedLocal.tombstones, CONFIG_COLLECTIONS), remoteConfig.tombstones);
  const dataNeedsPush = remoteData === null ||
    !contentEqual(mergedLocal.transactions, remoteData.transactions) ||
    !contentEqual(mergedLocal.statements, remoteData.statements) ||
    !contentEqual(tombstonesFor(mergedLocal.tombstones, DATA_COLLECTIONS), remoteData.tombstones);

  let configETag = remoteConfigDoc?.eTag ?? null;
  let dataETag = remoteDataDoc?.eTag ?? null;

  try {
    if (configNeedsPush) {
      const written = await writeDocument(
        CONFIG_DOCUMENT,
        buildConfigDocument(mergedLocal),
        remoteConfigDoc?.eTag ?? null,
      );
      configETag = written.eTag;
    }
    if (dataNeedsPush) {
      const written = await writeDocument(
        DATA_DOCUMENT,
        buildDataDocument(mergedLocal),
        remoteDataDoc?.eTag ?? null,
      );
      dataETag = written.eTag;
    }
  } catch (error) {
    if (error instanceof ConflictError && mayRetry) {
      // The other device wrote between our read and our write. Everything we
      // merged is already saved locally, so re-running picks up their changes
      // and pushes the union.
      return runSync({ ...(await repo.getSyncMeta()) }, accountId, false);
    }
    throw error;
  }

  await repo.acknowledgeSync({
    revision,
    configETag,
    dataETag,
    lastSyncedAt: new Date().toISOString(),
    accountId,
    dirty: false,
  }, revision);

  return {
    status: 'ok',
    pulled: transactions.addedFromRemote,
    pushed: transactions.addedFromLocal,
    updated: transactions.updatedFromRemote,
    configBothChanged,
    detail: null,
  };
}
